import React, { useState, useEffect } from 'react';

interface DailyWriterProps {
  theme: {
    primary: string;
    secondary: string;
    accent: string;
    success: string;
    pink?: string;
    blue?: string;
  };
  totalWordCount: number;
  onApplyPrompt: (prompt: string) => void;
}

interface WritingGoal {
  id: string;
  name: string;
  target: number;
  icon: string;
  description: string;
}

interface WritingStreak {
  currentStreak: number;
  longestStreak: number;
  lastWriteDate: string;
  totalDays: number;
}

const DailyWriter: React.FC<DailyWriterProps> = ({ theme, totalWordCount, onApplyPrompt }) => {
  const [selectedGoal, setSelectedGoal] = useState('casual');
  const [todayWords, setTodayWords] = useState(0);
  const [streak, setStreak] = useState<WritingStreak>({
    currentStreak: 0,
    longestStreak: 0,
    lastWriteDate: '',
    totalDays: 0
  });
  const [showCelebration, setShowCelebration] = useState(false);
  const [dailyChallenge, setDailyChallenge] = useState('');

  const writingGoals: WritingGoal[] = [
    { id: 'casual', name: 'Casual Writer', target: 50, icon: '✏️', description: '50 words - Just getting started!' },
    { id: 'steady', name: 'Steady Scribe', target: 100, icon: '📝', description: '100 words - Building the habit' },
    { id: 'dedicated', name: 'Dedicated Author', target: 200, icon: '📚', description: '200 words - Serious writing time' },
    { id: 'champion', name: 'Word Champion', target: 300, icon: '👑', description: '300 words - Ultimate challenge!' }
  ];

  const dailyChallenges = [
    "Write a scene where your character discovers something unexpected in their backpack",
    "Describe a conversation between your character and someone they just met",
    "Write about a moment when your character felt really brave",
    "Create a scene where your character has to solve a problem using only three objects",
    "Write about your character's perfect day",
    "Describe a place your character has never been but dreams of visiting",
    "Write a scene where your character learns something new about a friend",
    "Create a moment where your character has to make a difficult choice",
    "Write about a time when your character felt proud of themselves",
    "Describe your character's favorite hiding spot and why it's special"
  ];

  const motivationalMessages = [
    "Every great story starts with a single word! ✨",
    "You're building something amazing, one word at a time! 🏗️",
    "Writers are explorers of imagination! 🗺️",
    "Your creativity is your superpower! 💫",
    "Stories have the power to change the world! 🌟",
    "Keep writing - your characters are counting on you! 📖"
  ];

  useEffect(() => {
    // Load writing data from memory (simulating localStorage)
    const today = new Date().toDateString();
    setDailyChallenge(dailyChallenges[Math.floor(Math.random() * dailyChallenges.length)]);
    
    // In a real app, you'd load this from storage
    // For now, we'll simulate some progress
    setTodayWords(Math.min(totalWordCount, 150)); // Simulate today's progress
    setStreak({
      currentStreak: 3,
      longestStreak: 7,
      lastWriteDate: today,
      totalDays: 15
    });
  }, [totalWordCount]);

  const currentGoal = writingGoals.find(goal => goal.id === selectedGoal) || writingGoals[0];
  const progressPercentage = Math.min((todayWords / currentGoal.target) * 100, 100);
  const isGoalComplete = todayWords >= currentGoal.target;

  const getStreakIcon = () => {
    if (streak.currentStreak >= 7) return '🔥';
    if (streak.currentStreak >= 3) return '⚡';
    if (streak.currentStreak >= 1) return '✨';
    return '🌱';
  };

  const getEncouragementMessage = () => {
    if (isGoalComplete) return "Amazing work today! You crushed your goal! 🎉";
    if (progressPercentage >= 75) return "So close! You've got this! 💪";
    if (progressPercentage >= 50) return "Halfway there! Keep the momentum going! ⚡";
    if (progressPercentage >= 25) return "Great start! Every word counts! ✨";
    return "Ready to begin today's writing adventure? 🚀";
  };

  const writingTips = [
    {
      icon: '⏰',
      title: 'Set a Timer',
      tip: 'Try writing for just 10 minutes without stopping!',
      color: theme.primary
    },
    {
      icon: '🎵',
      title: 'Create Atmosphere', 
      tip: 'Play some background music that matches your story\'s mood',
      color: theme.secondary
    },
    {
      icon: '📱',
      title: 'Minimize Distractions',
      tip: 'Put away anything that might interrupt your creative flow',
      color: theme.blue || '#3498DB'
    },
    {
      icon: '🎯',
      title: 'Start Small',
      tip: 'Even writing one sentence is progress - celebrate small wins!',
      color: theme.success
    },
    {
      icon: '🌟',
      title: 'Write First, Edit Later',
      tip: 'Let your ideas flow freely, you can polish them later',
      color: theme.accent
    },
    {
      icon: '📝',
      title: 'Keep a Writing Journal',
      tip: 'Jot down story ideas whenever they come to you',
      color: theme.pink || '#E91E63'
    }
  ];

  return (
    <div style={{
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.primary} 50%, ${theme.accent} 100%)`,
      minHeight: '100vh',
      padding: '16px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '25px',
          padding: '30px',
          marginBottom: '25px',
          boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
          border: `4px solid ${theme.primary}`,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute', top: '10%', left: '8%', width: '30px', height: '30px',
            background: theme.pink || '#E91E63', borderRadius: '50%', opacity: 0.15
          }} />
          <div style={{
            position: 'absolute', bottom: '15%', right: '10%', width: '25px', height: '25px',
            background: theme.success, borderRadius: '50%', opacity: 0.2
          }} />
          
          <div style={{
            width: '80px', height: '80px',
            background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
            borderRadius: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', color: 'white', boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
            fontSize: '35px'
          }}>
            📅
          </div>
          
          <h1 style={{
            fontSize: '2.5rem', fontWeight: '900', margin: '0 0 12px 0',
            background: `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
          }}>
            Daily Writer Challenge
          </h1>
          <p style={{ fontSize: '1.2rem', color: '#666', fontWeight: '600', margin: 0 }}>
            Build your writing habit one word at a time!
          </p>
        </div>

        {/* Daily Progress */}
        <div style={{
          background: 'white', borderRadius: '20px', padding: '25px', marginBottom: '25px',
          boxShadow: '0 12px 35px rgba(0,0,0,0.1)', border: `3px solid ${theme.success}`
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'
          }}>
            <h3 style={{
              margin: 0, fontSize: '1.6rem', fontWeight: '800',
              background: `linear-gradient(45deg, ${theme.success}, ${theme.primary})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>
              Today's Progress
            </h3>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              background: `linear-gradient(135deg, ${theme.success}, ${theme.primary})`,
              color: 'white', padding: '8px 16px', borderRadius: '15px',
              fontSize: '14px', fontWeight: '700', boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}>
              <span style={{ fontSize: '20px' }}>{getStreakIcon()}</span>
              <span>{streak.currentStreak} day streak!</span>
            </div>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: `${theme.primary}10`, padding: '8px 12px', borderRadius: '12px'
              }}>
                <span style={{ fontSize: '20px' }}>{currentGoal.icon}</span>
                <span style={{ fontSize: '16px', fontWeight: '700', color: theme.primary }}>{currentGoal.name}</span>
              </div>
              <span style={{ fontSize: '16px', color: '#666', fontWeight: '600' }}>
                {todayWords}/{currentGoal.target} words
              </span>
            </div>
            <div style={{ 
              background: '#E5E5E5', borderRadius: '15px', height: '20px', overflow: 'hidden',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div 
                style={{ 
                  background: isGoalComplete ? 
                    `linear-gradient(45deg, ${theme.success}, ${theme.primary})` : 
                    `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`, 
                  height: '100%', 
                  width: `${progressPercentage}%`, 
                  borderRadius: '15px', 
                  transition: 'width 0.5s ease',
                  boxShadow: isGoalComplete ? `0 0 15px ${theme.success}60` : `0 0 10px ${theme.primary}40`,
                  position: 'relative'
                }}
              >
                {progressPercentage > 20 && (
                  <div style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    color: 'white', fontSize: '12px', fontWeight: '700'
                  }}>
                    {Math.round(progressPercentage)}%
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div style={{
            textAlign: 'center', fontSize: '16px', fontWeight: '700',
            background: `linear-gradient(45deg, ${theme.primary}, ${theme.success})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
          }}>
            {getEncouragementMessage()}
          </div>
        </div>

        {/* Goal Selection */}
        <div style={{
          background: 'white', borderRadius: '20px', padding: '25px', marginBottom: '25px',
          boxShadow: '0 12px 35px rgba(0,0,0,0.1)', border: `3px solid ${theme.secondary}`
        }}>
          <h3 style={{
            margin: '0 0 20px 0', fontSize: '1.6rem', fontWeight: '800',
            background: `linear-gradient(45deg, ${theme.secondary}, ${theme.accent})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>
            Choose Your Daily Goal
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
            {writingGoals.map((goal) => (
              <button
                key={goal.id}
                onClick={() => setSelectedGoal(goal.id)}
                style={{
                  background: selectedGoal === goal.id ? 
                    `linear-gradient(135deg, ${theme.accent}, ${theme.primary})` : 
                    'white',
                  border: `3px solid ${selectedGoal === goal.id ? theme.accent : '#ddd'}`,
                  padding: '20px',
                  borderRadius: '18px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '700',
                  color: selectedGoal === goal.id ? 'white' : '#333',
                  transition: 'all 0.3s ease',
                  textAlign: 'center',
                  boxShadow: selectedGoal === goal.id ? 
                    `0 8px 25px ${theme.accent}40` : 
                    '0 4px 15px rgba(0,0,0,0.08)'
                }}
                onMouseEnter={(e) => {
                  if (selectedGoal !== goal.id) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.borderColor = theme.accent;
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedGoal !== goal.id) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = '#ddd';
                  }
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>{goal.icon}</div>
                <div style={{ marginBottom: '6px', fontSize: '16px' }}>{goal.name}</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>{goal.target} words daily</div>
              </button>
            ))}
          </div>
        </div>

        {/* Daily Challenge */}
        <div style={{
          background: 'white', borderRadius: '20px', padding: '25px', marginBottom: '25px',
          boxShadow: '0 12px 35px rgba(0,0,0,0.1)', border: `3px solid ${theme.success}`
        }}>
          <h3 style={{
            margin: '0 0 20px 0', fontSize: '1.6rem', fontWeight: '800',
            background: `linear-gradient(45deg, ${theme.success}, ${theme.blue || '#3498DB'})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
          }}>
            🎯 Today's Writing Challenge
          </h3>
          
          <div style={{ 
            background: `${theme.success}10`, 
            border: `3px solid ${theme.success}30`,
            borderRadius: '18px', 
            padding: '20px',
            marginBottom: '20px'
          }}>
            <p style={{ 
              margin: '0', 
              fontSize: '16px', 
              lineHeight: '1.5', 
              color: '#333',
              fontStyle: 'italic',
              fontWeight: '500',
              textAlign: 'center'
            }}>
              "{dailyChallenge}"
            </p>
          </div>
          
          <button
            onClick={() => onApplyPrompt(dailyChallenge)}
            style={{
              width: '100%',
              background: `linear-gradient(135deg, ${theme.success}, ${theme.primary})`,
              color: 'white',
              border: 'none',
              padding: '18px',
              borderRadius: '15px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '800',
              transition: 'all 0.3s ease',
              boxShadow: `0 8px 25px ${theme.success}40`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = `0 12px 35px ${theme.success}60`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 8px 25px ${theme.success}40`;
            }}
          >
            🚀 Accept This Challenge!
          </button>
        </div>

        {/* Writing Stats */}
        <div style={{
          background: 'white', borderRadius: '20px', padding: '25px', marginBottom: '25px',
          boxShadow: '0 12px 35px rgba(0,0,0,0.1)', border: `3px solid ${theme.accent}`
        }}>
          <h3 style={{
            margin: '0 0 20px 0', fontSize: '1.6rem', fontWeight: '800',
            background: `linear-gradient(45deg, ${theme.accent}, ${theme.pink || '#E91E63'})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>
            📊 Your Writing Journey
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
            gap: '15px'
          }}>
            <div style={{
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
              color: 'white', padding: '20px', borderRadius: '15px', textAlign: 'center',
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '4px' }}>{streak.currentStreak}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, fontWeight: '600' }}>Current Streak</div>
            </div>
            
            <div style={{
              background: `linear-gradient(135deg, ${theme.success}, ${theme.blue || '#3498DB'})`,
              color: 'white', padding: '20px', borderRadius: '15px', textAlign: 'center',
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '4px' }}>{streak.longestStreak}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, fontWeight: '600' }}>Best Streak</div>
            </div>
            
            <div style={{
              background: `linear-gradient(135deg, ${theme.secondary}, ${theme.pink || '#E91E63'})`,
              color: 'white', padding: '20px', borderRadius: '15px', textAlign: 'center',
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '4px' }}>{streak.totalDays}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, fontWeight: '600' }}>Total Days</div>
            </div>
            
            <div style={{
              background: `linear-gradient(135deg, ${theme.accent}, ${theme.primary})`,
              color: 'white', padding: '20px', borderRadius: '15px', textAlign: 'center',
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '4px' }}>{totalWordCount}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, fontWeight: '600' }}>Total Words</div>
            </div>
          </div>
        </div>

        {/* Writing Tips */}
        <div style={{
          background: 'white', borderRadius: '20px', padding: '25px', marginBottom: '25px',
          boxShadow: '0 12px 35px rgba(0,0,0,0.1)', border: `3px solid ${theme.blue || '#3498DB'}`
        }}>
          <h3 style={{
            margin: '0 0 20px 0', fontSize: '1.6rem', fontWeight: '800',
            background: `linear-gradient(45deg, ${theme.blue || '#3498DB'}, ${theme.primary})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>
            💡 Daily Writing Tips
          </h3>
          
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '15px', maxHeight: '400px', overflowY: 'auto'
          }}>
            {writingTips.map((tip, index) => (
              <div 
                key={index}
                style={{ 
                  background: `${tip.color}10`, 
                  border: `3px solid ${tip.color}30`,
                  borderRadius: '15px', 
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = tip.color;
                  e.currentTarget.style.boxShadow = `0 6px 20px ${tip.color}30`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = tip.color + '30';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <span style={{ fontSize: '28px' }}>{tip.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                    {tip.title}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
                    {tip.tip}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Motivational Footer */}
        <div style={{
          background: 'white', borderRadius: '20px', padding: '25px',
          boxShadow: '0 12px 35px rgba(0,0,0,0.1)', border: `3px solid ${theme.pink || '#E91E63'}`,
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '18px', fontWeight: '800', marginBottom: '10px',
            background: `linear-gradient(45deg, ${theme.pink || '#E91E63'}, ${theme.primary})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
          }}>
            {motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)]}
          </div>
          <div style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>
            Remember: Every professional writer started with a single word. You've got this! 🌟
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyWriter;